import type { ApiMatchDTO } from "@/entities/bracket/api/types";

export type MatchUserRole =
  | "captain"
  | "participant"
  | "spectator"
  | "organizer";

export type MatchStepId =
  | "tauri_running"
  | "predraft_done"
  | "lobby_created"
  | "all_in_lobby"
  | "opponent_ready"
  | "in_game"
  | "result_submitted";

export type MatchStepStatus = "done" | "active" | "pending";

export interface MatchStep {
  id: MatchStepId;
  status: MatchStepStatus;
}

export interface MatchChecklistInput {
  match: Pick<ApiMatchDTO, "status" | "phase"> | null | undefined;
  tournamentType: string | null | undefined;
  role: MatchUserRole;
  isInTauri: boolean;
  lcuConnected: boolean;
  lcuPhase?: string | null | undefined;
  lobbyCreated?: boolean;
  resultSubmitted?: boolean;
}

// Result submission НЕ включён в чек-лист участников — Tauri шлёт результат
// автоматически через /lcu-result после EndOfGame. Если автоотправка падает,
// есть EndOfGame fallback (G) + админ может вручную закрыть матч.
// Юзеру не нужно видеть «Подтвердите результат» как действие.
const CLASSIC_STEPS: MatchStepId[] = [
  "tauri_running",
  "predraft_done",
  "lobby_created",
  "all_in_lobby",
  "in_game",
  // KAN-178: шаг был потерян в 4442cb8d (live-data pipeline) — при этом тип,
  // ветка в computeStatus и обработка в isDone*/isActive* для него остались,
  // т.е. удаление было случайным. Полтора месяца в чек-листе не было финального
  // шага «результат отправлен».
  "result_submitted",
];

const SOLO_STEPS: MatchStepId[] = [
  "tauri_running",
  "opponent_ready",
  "lobby_created",
  "in_game",
  "result_submitted", // KAN-178: см. коммент у CLASSIC_STEPS
];

const SPECTATOR_STEPS: MatchStepId[] = ["in_game", "result_submitted"]; // KAN-178

function normalizePhase(value: string | null | undefined): string {
  return String(value ?? "").trim().toLowerCase();
}

function isDoneByMatchPhase(phase: string, step: MatchStepId): boolean {
  switch (step) {
    case "predraft_done":
      return (
        phase === "ingame" ||
        phase === "resultuploaded" ||
        phase === "confirmed"
      );
    case "lobby_created":
    case "opponent_ready":
      return (
        phase === "ingame" ||
        phase === "resultuploaded" ||
        phase === "confirmed"
      );
    case "all_in_lobby":
      return (
        phase === "ingame" ||
        phase === "resultuploaded" ||
        phase === "confirmed"
      );
    case "in_game":
      return phase === "resultuploaded" || phase === "confirmed";
    case "result_submitted":
      return phase === "confirmed";
    default:
      return false;
  }
}

function isActiveByMatchPhase(phase: string, step: MatchStepId): boolean {
  switch (step) {
    case "predraft_done":
      return phase === "lobbypending" || phase === "lobbycreated" || phase === "championselect";
    case "lobby_created":
    case "opponent_ready":
      return (
        phase === "" ||
        phase === "lobbypending" ||
        phase === "lobbycreated" ||
        phase === "championselect"
      );
    case "all_in_lobby":
      return phase === "championselect";
    case "in_game":
      return phase === "ingame";
    case "result_submitted":
      return phase === "resultuploaded";
    default:
      return false;
  }
}

export function getMatchChecklist(input: MatchChecklistInput): MatchStep[] {
  const { match, tournamentType, role, isInTauri, lcuConnected } = input;
  const lobbyCreated = Boolean(input.lobbyCreated);
  const resultSubmitted = Boolean(input.resultSubmitted);
  const phase = normalizePhase(match?.phase);
  const status = normalizePhase(match?.status);

  const isSolo = tournamentType === "SoloOneVsOne";
  const stepIds =
    role === "spectator" || role === "organizer"
      ? SPECTATOR_STEPS
      : isSolo
        ? SOLO_STEPS
        : CLASSIC_STEPS;

  const computeStatus = (step: MatchStepId): MatchStepStatus => {
    if (step === "tauri_running") {
      if (isInTauri && lcuConnected) return "done";
      return "active";
    }

    if (resultSubmitted || status === "completed") {
      if (step === "result_submitted") return "done";
    }

    if (step === "lobby_created" && lobbyCreated) {
      return isDoneByMatchPhase(phase, step) ? "done" : "done";
    }

    if (isDoneByMatchPhase(phase, step)) return "done";
    if (isActiveByMatchPhase(phase, step)) return "active";

    return "pending";
  };

  const steps = stepIds.map<MatchStep>((id) => ({
    id,
    status: computeStatus(id),
  }));

  const hasActive = steps.some((s) => s.status === "active");
  if (!hasActive) {
    const firstPendingIdx = steps.findIndex((s) => s.status === "pending");
    if (firstPendingIdx !== -1) {
      const step = steps[firstPendingIdx]!;
      steps[firstPendingIdx] = { ...step, status: "active" };
    }
  }

  return steps;
}

export function resolveMatchUserRole(input: {
  userId: string | null | undefined;
  teamACaptainId: string | null | undefined;
  teamBCaptainId: string | null | undefined;
  teamAMemberIds: string[];
  teamBMemberIds: string[];
  isOrganizer: boolean;
}): MatchUserRole {
  const { userId, isOrganizer } = input;
  if (!userId) return isOrganizer ? "organizer" : "spectator";
  if (userId === input.teamACaptainId || userId === input.teamBCaptainId) {
    return "captain";
  }
  if (
    input.teamAMemberIds.includes(userId) ||
    input.teamBMemberIds.includes(userId)
  ) {
    return "participant";
  }
  return isOrganizer ? "organizer" : "spectator";
}
